import { MouseEvent, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  Alert,
  Link as MuiLink,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { UseMutationResult } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import * as Sentry from '@sentry/capacitor';
import PaypalLogo from 'assets/paypal_logo.svg?react';
import PaypalLogoDisabled from 'assets/paypal_logo_disabled.svg?react';
import { RecurlySavedPayment } from 'features/payments/components/RecurlySavedPayment';
import {
  DonationCheckoutConfig,
  PaymentMethodData,
  ProcessDonationPaymentTokenData,
  ProcessInitialDonationPaymentTokenData,
  ProcessPaymentState,
  ProcessMembershipPaymentTokenData,
  ProcessMembershipUpgradeData,
  MembershipCheckoutConfig,
} from 'shared/types';
import { formatToUSDollars } from 'shared/utils';
import { useEmailValidation } from 'hooks/useEmailValidation';
import { EmailStatus } from 'shared/getEmailValidationSchema';
import { usePurchaseTracking } from 'hooks/usePurchaseTracking';
import { recurlyOnErrorHandler } from 'features/payments/payments.utils';
import { Loader } from 'components/Loader';
import { ApplePayButton } from './ApplePayButton';
import { usePayments } from './usePayments';
import { CheckoutPriceSummary } from '../CheckoutPriceSummary';
import {
  FormValues,
  getValidationSchema,
  getPaymentText,
  isFieldDisabled,
} from './utils';

type MembershipAccountCheckoutConfig = MembershipCheckoutConfig & {
  upgrade: boolean;
};

type RecurlyAccountInformationProps<T> = {
  initialState: ProcessPaymentState;
  updateState: (newState: Partial<ProcessPaymentState>) => void;
  processPaymentMutation: UseMutationResult<void, Error, T, unknown>;
  paymentMethod?: PaymentMethodData;
} & (
  | {
      transactionType: 'membership';
      checkoutConfig: MembershipAccountCheckoutConfig;
    }
  | {
      transactionType: 'donation';
      checkoutConfig: DonationCheckoutConfig;
    }
);

const useStyles = makeStyles()(() => ({
  cardIcon: {
    width: 24,
    height: 24,
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export const RecurlyAccountInformation = <
  T extends
    | ProcessMembershipPaymentTokenData
    | ProcessInitialDonationPaymentTokenData
    | ProcessDonationPaymentTokenData
    | ProcessMembershipUpgradeData,
>(
  props: RecurlyAccountInformationProps<T>,
): JSX.Element => {
  const {
    transactionType,
    initialState,
    updateState,
    processPaymentMutation,
    paymentMethod,
    checkoutConfig,
  } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { logPurchaseEvent } = usePurchaseTracking();

  const isMembershipTransaction = transactionType === 'membership';
  const membershipCheckoutConfig = isMembershipTransaction
    ? checkoutConfig
    : undefined;

  const {
    clientToken,
    transactionAmount,
    donationRenews,
    existingUser,
    verificationToken,
    userId,
  } = initialState;

  const initialValues: FormValues = useMemo(() => {
    return {
      firstName: initialState.firstName,
      lastName: initialState.lastName,
      email: initialState.email,
      optInToUpdates: initialState.optInToUpdates,
    };
  }, [
    initialState.email,
    initialState.firstName,
    initialState.lastName,
    initialState.optInToUpdates,
  ]);

  const emailValidationCache = useRef<Map<string, EmailStatus>>(new Map());

  const {
    watch,
    control,
    formState: { isValid, errors, isValidating },
    trigger,
  } = useForm<FormValues>({
    resolver: yupResolver(
      getValidationSchema(
        t,
        Boolean(existingUser),
        emailValidationCache.current,
      ),
    ),
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const email = watch('email');
  const optInToUpdates = watch('optInToUpdates');

  const { mutate, isPending } = processPaymentMutation;

  const processMembershipPayment = (
    token?: string,
    selectedPaymentMethod?: 'paypal' | 'apple_pay',
  ): void => {
    if (token && !clientToken) return;

    if (!token && (!userId || !verificationToken)) return;

    if (!membershipCheckoutConfig) return;

    const data: ProcessMembershipPaymentTokenData = {
      email,
      planCode: membershipCheckoutConfig.code,
      optInToUpdates,
    };

    if (userId && verificationToken) {
      data.userId = userId;
      data.verificationToken = verificationToken;
    } else {
      data.authorizationToken = token;
      data.clientToken = clientToken;
      data.firstName = firstName;
      data.lastName = lastName;
    }

    mutate(data as T, {
      onSuccess: () => {
        const method = selectedPaymentMethod || paymentMethod?.paymentType;
        logPurchaseEvent({
          productId: membershipCheckoutConfig.code,
          price: transactionAmount || 0,
          transactionType: 'membership',
          paymentMethod: method,
          isUpgrade: membershipCheckoutConfig.upgrade,
        });
      },
      onError: (err: unknown) => {
        recurlyOnErrorHandler(err, t);
      },
    });
  };

  const processInitialDonationPayment = (
    token: string,
    selectedPaymentMethod?: 'paypal' | 'apple_pay',
  ): void => {
    if (!clientToken) return;

    const data: ProcessInitialDonationPaymentTokenData = {
      clientToken,
      donationAmount: transactionAmount,
      donationRenews: Boolean(donationRenews),
      firstName,
      lastName,
      email,
      optInToUpdates,
      authorizationToken: token,
    };

    mutate(data as T, {
      onSuccess: () => {
        const method = selectedPaymentMethod || paymentMethod?.paymentType;
        logPurchaseEvent({
          productId: donationRenews ? 'donation_recurring' : 'donation_onetime',
          price: transactionAmount || 0,
          transactionType: 'donation',
          paymentMethod: method,
          isUpgrade: false,
        });
      },
      onError: (err: unknown) => {
        recurlyOnErrorHandler(err, t);
      },
    });
  };

  const processReturningDonationPayment = (
    token?: string,
    selectedPaymentMethod?: 'paypal' | 'apple_pay',
  ): void => {
    if (!userId || !verificationToken) return;

    const data: ProcessDonationPaymentTokenData = {
      userId,
      verificationToken,
      donationAmount: transactionAmount,
      donationRenews: Boolean(donationRenews),
    };

    if (token) {
      data.authorizationToken = token;
    }

    mutate(data as T, {
      onSuccess: () => {
        const method =
          selectedPaymentMethod ||
          (paymentMethod?.paymentType === 'paypal_billing_agreement'
            ? 'paypal'
            : paymentMethod?.paymentType) ||
          'credit_card';
        const donationProductId = donationRenews
          ? 'donation_recurring'
          : 'donation_onetime';
        logPurchaseEvent({
          productId: donationProductId,
          price: transactionAmount || 0,
          transactionType: 'donation',
          paymentMethod: method,
          isUpgrade: false,
        });
      },
      onError: (err: unknown) => {
        recurlyOnErrorHandler(err, t);
      },
    });
  };

  const submitToken = (
    token: string,
    selectedPaymentMethod?: 'paypal' | 'apple_pay',
  ): void => {
    if (isMembershipTransaction) {
      processMembershipPayment(token, selectedPaymentMethod);
      return;
    }

    if (clientToken) {
      processInitialDonationPayment(token, selectedPaymentMethod);
      return;
    }

    if (userId && verificationToken) {
      processReturningDonationPayment(token, selectedPaymentMethod);
    }
  };

  const { applePaySupported, applePay, payPal } = usePayments({
    isMembershipTransaction: transactionType === 'membership',
    transactionAmount,
    // "submitToken" is not used as a dependency of any other hook/custom hooks in "usePayments", so no need to memoize
    submitToken,
  });

  const handleUpdatePaymentMethod = (
    event: MouseEvent<HTMLAnchorElement>,
  ): void => {
    if (!isValid) {
      event.preventDefault();
      trigger();
      return;
    }

    updateState({
      firstName,
      lastName,
      email,
      optInToUpdates,
      updatePaymentMethod: true,
    });
  };

  const processPaymentWithSavedMethod = (): void => {
    if (isMembershipTransaction) {
      if (membershipCheckoutConfig?.upgrade) {
        const upgradePlanCode = membershipCheckoutConfig.code;

        if (!verificationToken || !userId) {
          Sentry.captureMessage(
            'Missing parameters, membership upgrade aborted',
            {
              level: 'error',
              extra: {
                verificationToken: verificationToken || '',
                userId: userId || '',
                upgradePlanCode,
                fromIap: false,
              },
            },
          );
          return;
        }

        const data: ProcessMembershipUpgradeData = {
          verificationToken,
          userId,
          upgradePlanCode,
          fromIap: false,
        };

        mutate(data as T, {
          onSuccess: () => {
            logPurchaseEvent({
              productId: upgradePlanCode,
              price: transactionAmount || 0,
              transactionType: 'membership',
              paymentMethod: 'credit_card',
              isUpgrade: true,
            });
          },
          onError: (err: unknown) => {
            recurlyOnErrorHandler(err, t);
          },
        });
        return;
      }

      processMembershipPayment();
      return;
    }

    processReturningDonationPayment();
  };

  const handleGoToCC = (): void => {
    updateState({
      firstName,
      lastName,
      email,
      optInToUpdates,
      updatePaymentMethod: false,
    });
  };

  const { emailWarningText, emailFieldSxProps } = useEmailValidation({
    fieldKey: 'email',
    errors,
    watch,
  });

  if (isPending) {
    return <Loader disableBackground />;
  }

  const displayFirstNameInput =
    !paymentMethod || (paymentMethod && !initialValues.firstName);
  const displayLastNameInput =
    !paymentMethod || (paymentMethod && !initialValues.lastName);

  const paymentMethodText = getPaymentText(
    t,
    isMembershipTransaction,
    membershipCheckoutConfig?.upgrade,
    transactionAmount,
  );

  const formattedPrice = formatToUSDollars(transactionAmount, 2);

  return (
    <Box
      component="form"
      noValidate
      sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
    >
      <Stack spacing={2} sx={{ flex: 1, mb: 3 }}>
        <Typography variant="h2">
          <b>{t('recurlyAccountInformation.title')}</b>
        </Typography>

        {errors.email?.type === 'managed' && (
          <Alert icon={false} severity="error" variant="standard">
            <Typography sx={{ marginBottom: '16px' }}>
              {errors.email.message}
            </Typography>

            <Typography>{t('organization.contactYourAdmin')}</Typography>
          </Alert>
        )}

        {errors.email?.type === 'registered' && (
          <Alert icon={false} severity="error" variant="standard">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'medium',
                marginBottom: '16px',
              }}
            >
              {errors.email.message}
            </Typography>

            <MuiLink
              to="/login"
              component={Link}
              color="inherit"
              underline="always"
              variant="body2"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {t('recurlyAccountInformation.login')}
            </MuiLink>
          </Alert>
        )}

        {(displayFirstNameInput || displayLastNameInput) && (
          <Grid container spacing={2} sx={{ width: '100%' }}>
            {displayFirstNameInput && (
              <Grid size={6}>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field, fieldState }): JSX.Element => {
                    const { ref, ...muiFieldProps } = field;
                    return (
                      <TextField
                        id="field-control-firstName"
                        label={t(
                          'recurlyAccountInformation.inputs.firstName.label',
                        )}
                        fullWidth
                        {...muiFieldProps}
                        inputRef={ref}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        required
                        disabled={isFieldDisabled(
                          isMembershipTransaction,
                          initialState,
                          initialValues.firstName,
                        )}
                      />
                    );
                  }}
                />
              </Grid>
            )}

            {displayLastNameInput && (
              <Grid size={6}>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field, fieldState }): JSX.Element => {
                    const { ref, ...muiFieldProps } = field;
                    return (
                      <TextField
                        id="field-control-lastName"
                        label={t(
                          'recurlyAccountInformation.inputs.lastName.label',
                        )}
                        fullWidth
                        {...muiFieldProps}
                        inputRef={ref}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        required
                        disabled={isFieldDisabled(
                          isMembershipTransaction,
                          initialState,
                          initialValues.lastName,
                        )}
                      />
                    );
                  }}
                />
              </Grid>
            )}
          </Grid>
        )}

        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            const { ref, ...muiFieldProps } = field;
            return (
              <TextField
                id="field-control-email"
                label={t('recurlyAccountInformation.inputs.email.label')}
                fullWidth
                type="email"
                {...muiFieldProps}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={
                  (fieldState.error?.type !== 'registered' &&
                  fieldState.error?.type !== 'managed'
                    ? fieldState.error?.message
                    : undefined) || emailWarningText
                }
                required
                disabled={existingUser}
                color={emailWarningText ? 'warning' : undefined}
                focused={Boolean(emailWarningText)}
                sx={emailFieldSxProps}
              />
            );
          }}
        />

        {!userId && (
          <Controller
            name="optInToUpdates"
            control={control}
            render={({ field }): JSX.Element => {
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      name={field.name}
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label={t(
                    'recurlyAccountInformation.inputs.optInToUpdates.label',
                  )}
                />
              );
            }}
          />
        )}

        {!paymentMethod && Boolean(userId) && (
          <Typography variant="h3" sx={{ mt: 2 }}>
            {t('recurlyAccountInformation.paymentInformation')}
          </Typography>
        )}

        {Boolean(paymentMethod) && (
          <>
            <Typography variant="h3" sx={{ mt: 3 }}>
              {t('recurlyAccountInformation.paymentMethod.title')}
            </Typography>

            <Card variant="outlined" sx={{ borderRadius: '8px' }}>
              <CardContent
                sx={{
                  pb: '16px !important',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <RecurlySavedPayment paymentMethod={paymentMethod!} />

                <Button
                  fullWidth
                  size="large"
                  variant="outlined"
                  color="neutral"
                  component={Link}
                  to={
                    isMembershipTransaction
                      ? '/membership/billing_info'
                      : '/donate/billing_info'
                  }
                  onClick={handleUpdatePaymentMethod}
                >
                  {t('recurlyAccountInformation.updatePaymentMethod')}
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </Stack>

      {paymentMethod ? (
        <Stack spacing={2}>
          <CheckoutPriceSummary
            title={t('recurlyAccountInformation.total')}
            price={
              !isMembershipTransaction && donationRenews ? (
                <>
                  <span>{formattedPrice} </span>
                  <Typography component="span" color="secondary">
                    {t('donate.perMonth')}
                  </Typography>
                </>
              ) : (
                formattedPrice
              )
            }
            description={checkoutConfig.description}
          />

          <Button
            fullWidth
            size="large"
            onClick={processPaymentWithSavedMethod}
          >
            {paymentMethodText}
          </Button>
        </Stack>
      ) : (
        <Stack spacing={1}>
          {(!isMembershipTransaction || !userId) && (
            <Box sx={{ pb: 1 }}>
              <CheckoutPriceSummary
                title={checkoutConfig.title}
                price={formattedPrice}
                description={checkoutConfig.description}
              />
            </Box>
          )}

          <Button
            fullWidth
            size="large"
            startIcon={<CreditCardIcon className={classes.cardIcon} />}
            disabled={!isValid}
            component={Link}
            to={
              isMembershipTransaction
                ? '/membership/billing_info'
                : '/donate/billing_info'
            }
            onClick={handleGoToCC}
            data-testid="credit-card-button"
          >
            {isMembershipTransaction
              ? t('recurlyAccountInformation.button')
              : t('recurlyAccountInformation.donateButton')}
          </Button>

          {Boolean(applePay) && applePaySupported && (
            <ApplePayButton
              variant={isMembershipTransaction ? 'default' : 'donation'}
              onClick={() => applePay?.begin()}
              disabled={!isValid}
            />
          )}

          {payPal !== null && (
            <Button
              fullWidth
              size="large"
              onClick={() => payPal.start()}
              disabled={!isValid}
              aria-label={t('recurlyAccountInformation.payPal.button')}
              sx={!isValid ? { opacity: 0.7 } : {}}
            >
              {isValid ? <PaypalLogo /> : <PaypalLogoDisabled />}
            </Button>
          )}
        </Stack>
      )}

      {isValidating && <Loader disableBackground />}
    </Box>
  );
};
